"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import NextImage from "next/image";
import dots from "@/public/ecommerce/dots.png";
import Image from "@/helpers/Image";
import { CTAButton } from "@/shadcn/ui/button";
import { PageFuturesLPSchema } from "@/sanity/pages/PageFuturesLPTemplate";

export default function CTASection({ data }: { data: PageFuturesLPSchema }) {
  if (!data.ctaTitle && !data.heroPrice) return null;

  return (
    <section className="bg-white pb-[7rem] lg:pb-[12.5rem]">
      <div className="mx-auto w-[min(150rem,100%)] px-[4rem] flex flex-col items-center gap-[5rem]">
        {/* Header */}
        <div className="flex flex-col items-center gap-[2rem] text-center">
          {data.ctaChip && (
            <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
              {data.ctaChip}
            </p>
          )}
          {data.ctaTitle && (
            <div className="richtext richtext-center prose-h2:text-[5rem] lg:prose-h2:text-[7rem]">
              <PortableText
                value={data.ctaTitle}
                components={richTextComponent}
              />
            </div>
          )}
          {data.ctaBody && (
            <p className="mb-0 font-geist font-normal text-[2.25rem] leading-[1.5] text-black/70 max-w-[71.25rem]">
              {data.ctaBody}
            </p>
          )}
        </div>

        {/* Card + payment */}
        <div className="flex flex-col items-center gap-[6rem] w-full lg:w-[51.5rem]">
          {/* Dark pricing card */}
          <div className="relative bg-[#001b14] rounded-[0.25rem] p-[3rem] flex flex-col gap-[5rem] items-center overflow-hidden w-full">
            {/* Teal top accent */}
            <div className="absolute top-0 left-1/2 z-[1] -translate-x-1/2 h-[0.375rem] w-[32.625rem] bg-[#12f0b4]" />

            {/* Background texture */}
            <NextImage
              src={dots}
              alt=""
              fill
              className="object-cover opacity-15 mix-blend-luminosity"
            />

            {/* Price block */}
            <div className="relative z-10 flex flex-col items-center gap-[1rem] text-center w-full px-[1rem]">
              <div className="flex flex-col items-center gap-[0.5rem] leading-[1.3]">
                {data.heroPrice && (
                  <p className="mb-0 font-geist font-semibold text-[6.75rem] text-[#12f0b4] leading-none tracking-[-0.2rem]">
                    {data.heroPrice}
                  </p>
                )}
                {data.heroOriginalPrice && (
                  <p className="mb-0 font-geist font-normal text-[2.75rem] text-white line-through tracking-[-0.0825rem]">
                    {data.heroOriginalPrice}
                  </p>
                )}
              </div>
              {data.heroPriceDetails?.map((detail, i) => (
                <p
                  key={detail._key ?? i}
                  className="mb-0 font-geist font-normal text-[2rem] leading-[1.2] text-white/60 tracking-[-0.04rem]"
                >
                  {detail.text}
                </p>
              ))}
            </div>

            {/* CTA block */}
            <div className="relative z-10 flex flex-col gap-[1.5rem] items-center w-full">
              {data.ctaCTA && (
                <CTAButton data={data.ctaCTA} className="w-full lg:w-full" />
              )}
              {data.ctaSubtext && (
                <p className="mb-0 font-geist font-normal text-[2rem] leading-[1.2] text-white/60 text-center tracking-[-0.04rem]">
                  {data.ctaSubtext}
                </p>
              )}
            </div>
          </div>

          {/* Payment logos */}
          {(data.heroPaymentLabel || !!data.heroPaymentLogos?.length) && (
            <div className="flex flex-col items-center gap-[0.75rem] opacity-50">
              {data.heroPaymentLabel && (
                <p className="mb-0 font-geist font-medium text-[1.5rem] leading-[1.5] text-[#001b14] text-center tracking-[-0.03rem] uppercase">
                  {data.heroPaymentLabel}
                </p>
              )}
              {!!data.heroPaymentLogos?.length && (
                <div className="flex gap-[1.5rem] items-center justify-center flex-wrap">
                  {data.heroPaymentLogos.map((logo, i) => (
                    <Image
                      key={i}
                      src={logo}
                      alt=""
                      className="!h-[4.25rem] !w-auto object-contain shrink-0"
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
